import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../redux/store';
import { getPricingCatalog } from '../services/catalog.service';
import { PLAN_SLUG } from '../shared/plans/catalog';
import {
	CatalogAnnualPrice,
	CatalogPlan,
	CatalogPlanType,
	CatalogPrice,
	CatalogSection,
	CatalogUnitPrice,
} from '../shared/catalog/types';

/**
 * KILL SWITCH for Talon (per-user) pricing.
 * - true  → show the personalized `final` price returned by the backend.
 * - false → show every plan's `base` list price and zero discount.
 * Flip to false if Talon misbehaves in production. NOTE: this only changes
 * what we DISPLAY; the backend still decides the actual charge at checkout.
 */
const SHOW_TALON_PRICES = true;

/**
 * PARCHE TEMPORAL (2026-07-29): fuerza el primer mes gratis en los 3 planes
 * mensuales, que es lo que el checkout YA cobra.
 *
 * La campaña de Talon aplica UN descuento por sesión. `pricing/catalog` evalúa
 * los ~14 product versions de toda la grid en UNA sola sesión, así que la regla
 * dispara una vez, aterriza en RESICO, y los otros dos vuelven a precio de lista.
 * El carrito real manda un plan por sesión, así que ahí sí sale en 0.
 *
 * Verificado contra prod con `discount/preview` (usuario 601169):
 *   pv 139 solo → final 0 · pv 141 solo → final 0 · pv 143 solo → final 0
 *   pv 139+141+143 juntos → solo 141 en 0
 *
 * Esto solo corrige lo que se PINTA; el cobro ya era 0 y no lo toca. Bórralo en
 * cuanto el backend cotice cada product version en su propia sesión de Talon
 * (`PriceProductVersions` en heru-sales-transaction-engine).
 */
const FIRST_MONTH_FREE_SLUGS: ReadonlySet<string> = new Set([
	PLAN_SLUG.PLATAFORMAS,
	PLAN_SLUG.PROFESIONISTA,
	PLAN_SLUG.EMPRESARIAL,
]);

/**
 * ¿Este usuario está en la campaña del primer mes gratis?
 *
 * No se consulta su historial de suscripciones: TALON YA DECIDIÓ, y su
 * veredicto viene en la respuesta. Verificado contra prod, mismo endpoint,
 * tres perfiles (monthly.final en centavos):
 *
 *   sin historial   → plataformas 24900 · resico     0 · empresarial 199900
 *   canceló         → plataformas 18700 · resico 49900 · empresarial  90000
 *   sub activa (AG) → plataformas 17400 · resico 55900 · empresarial 199900
 *
 * Solo el que nunca ha tenido plan recibe un 0. Los otros dos traen su precio
 * real con el descuento de su aliado o su tier — que es justo lo que el parche
 * de abajo estaba borrando: a un suscriptor de AG le pintaba "primer mes
 * gratis, $799 tachado" cuando su precio real son $559 al mes.
 *
 * Preguntárselo a la respuesta y no al historial del front mantiene UNA sola
 * fuente de la elegibilidad (Talon) y se auto-apaga cuando la campaña termine.
 */
const isFirstMonthFreeEligible = (plans: CatalogPlan[]): boolean =>
	plans.some(
		(plan) =>
			FIRST_MONTH_FREE_SLUGS.has(plan.slug) && plan.monthly?.final === 0,
	);

const forceFirstMonthFree = (plan: CatalogPlan): CatalogPlan => {
	if (!plan.monthly || !FIRST_MONTH_FREE_SLUGS.has(plan.slug)) return plan;
	if (plan.monthly.final === 0) return plan;
	return {
		...plan,
		monthly: { ...plan.monthly, final: 0, discount: plan.monthly.base },
	};
};

export const CATALOG_QUERY_KEY = (userId?: number) =>
	['catalog', userId ?? 0] as const;

const TYPE_BY_SECTION: Record<CatalogSection, CatalogPlanType> = {
	subscriptions: 'subscription',
	annual: 'annual',
	asat: 'asat',
	regularizations: 'regularization',
	invoicing: 'invoicing',
};

const rebase = <T extends CatalogPrice>(price: T): T => ({
	...price,
	final: price.base,
	discount: 0,
});

const stripTalonFromPlan = (plan: CatalogPlan): CatalogPlan => {
	const monthly = plan.monthly ? rebase(plan.monthly) : undefined;

	let annual: CatalogAnnualPrice | undefined;
	if (plan.annual) {
		const annualBase = plan.annual.base;
		const perMonth = Math.round(annualBase / 12);
		const monthlyBase = plan.monthly?.base ?? 0;
		const savingsPerYear = Math.max(0, monthlyBase * 12 - annualBase);
		const savingsPct =
			monthlyBase > 0
				? Math.max(
						0,
						Math.round(((monthlyBase - perMonth) / monthlyBase) * 100),
				  )
				: 0;
		annual = {
			...plan.annual,
			final: annualBase,
			discount: 0,
			per_month: perMonth,
			savings_per_year: savingsPerYear,
			savings_pct: savingsPct,
		};
	}

	return {
		...plan,
		monthly,
		annual,
		one_time: plan.one_time ? rebase(plan.one_time) : undefined,
		unit: plan.unit ? (rebase(plan.unit) as CatalogUnitPrice) : undefined,
	};
};

/**
 * React Query access to the pricing catalog. The catalog is fetched ONCE
 * (`section=all`) and shared across every surface via a single query key; each
 * `useCatalog(section)` call just filters the cached products by their `type`.
 * Switching plans-page tabs (or opening any plan surface) needs no extra
 * request.
 */
export const useCatalog = (section: CatalogSection = 'subscriptions') => {
	const userId = useSelector((state: RootState) => state.profile.user?.id);

	const query = useQuery({
		queryKey: CATALOG_QUERY_KEY(userId),
		queryFn: getPricingCatalog,
		staleTime: 10 * 60 * 1000,
		// Wait for the logged-in user so the cache key is stable — otherwise the
		// query fires once with the placeholder key and again once the profile
		// loads, double-hitting the endpoint.
		enabled: userId != null,
	});

	const filtered = (query.data?.products ?? []).filter(
		(plan) => plan.type === TYPE_BY_SECTION[section],
	);
	// El parche del primer mes gratis solo corre para quien Talon declaró
	// elegible. Para todos los demás se respeta el precio que vino, que ya trae
	// su descuento de aliado o de tier.
	const plans: CatalogPlan[] = !SHOW_TALON_PRICES
		? filtered.map(stripTalonFromPlan)
		: isFirstMonthFreeEligible(filtered)
			? filtered.map(forceFirstMonthFree)
			: filtered;

	// Se evalúa sobre el catálogo COMPLETO y no sobre `filtered` para que la
	// respuesta no dependa de la sección que pidió el consumidor: la
	// elegibilidad es del usuario, no de la pantalla. Es la ÚNICA fuente de
	// "este usuario recibe el primer mes gratis" — pregúntale a esto, nunca al
	// historial de suscripciones, y así se apaga solo cuando Talon apague la
	// campaña.
	const firstMonthFree = isFirstMonthFreeEligible(query.data?.products ?? []);

	return { ...query, plans, firstMonthFree };
};

export type CatalogProductRef = { type: CatalogPlanType; slug: string };

/**
 * Map of `product_version_id` → `{ type, slug }` across ALL catalog products
 * and every price point (monthly / annual / one_time / unit). Reuses the same
 * cached catalog query as `useCatalog` (same key → no extra request). Lets the
 * checkout-intent deeplink executor resolve what a bare pv actually is, instead
 * of inferring the kind from URL suffixes.
 */
export const useCatalogProductMap = () => {
	const userId = useSelector((state: RootState) => state.profile.user?.id);

	const query = useQuery({
		queryKey: CATALOG_QUERY_KEY(userId),
		queryFn: getPricingCatalog,
		staleTime: 10 * 60 * 1000,
		enabled: userId != null,
	});

	const byPv = useMemo(() => {
		const map = new Map<number, CatalogProductRef>();
		for (const plan of query.data?.products ?? []) {
			for (const price of [
				plan.monthly,
				plan.annual,
				plan.one_time,
				plan.unit,
			]) {
				if (price?.product_version_id != null) {
					map.set(price.product_version_id, {
						type: plan.type,
						slug: plan.slug,
					});
				}
			}
		}
		return map;
	}, [query.data]);

	return { ...query, byPv };
};

/**
 * Map of `slug` → full `CatalogPlan` across ALL sections. Reuses the same cached
 * catalog query (same key → no extra request). Lets the `?product=<slug>`
 * deeplink resolve a product surface from a slug on any route.
 */
export const useCatalogPlanBySlug = () => {
	const userId = useSelector((state: RootState) => state.profile.user?.id);

	const query = useQuery({
		queryKey: CATALOG_QUERY_KEY(userId),
		queryFn: getPricingCatalog,
		staleTime: 10 * 60 * 1000,
		enabled: userId != null,
	});

	const bySlug = useMemo(() => {
		const map = new Map<string, CatalogPlan>();
		for (const plan of query.data?.products ?? []) map.set(plan.slug, plan);
		return map;
	}, [query.data]);

	return { ...query, bySlug };
};
